/**
 * PWA Update Utilities
 * Provides functions to handle PWA updates and cache clearing
 */

/**
 * Forces a hard reload bypassing all caches
 */
export const forceHardReload = (): void => {
  // Method 1: Location reload with cache bypass
  try {
    window.location.href = `${window.location.href}?v=${Date.now()}`;
  } catch {
    // Fallback method
    window.location.reload();
  }
};

/**
 * Clears all application caches
 */
export const clearAllCaches = async (): Promise<boolean> => {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log('Clearing caches:', cacheNames);

      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName))
      );

      console.log('All caches cleared successfully');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error clearing caches:', error);
    return false;
  }
};

/**
 * Clears browser storage (preserving essential auth tokens)
 */
export const clearStorageSelectively = (): void => {
  try {
    // Keys to preserve during updates
    const preserveKeys = [
      'access_token',
      'refresh_token',
      'user_profile',
      'company_profile',
      'user_roles'
    ];

    // Clear localStorage selectively
    if ('localStorage' in window) {
      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        const shouldPreserve = preserveKeys.some((preserve) =>
          key.toLowerCase().includes(preserve.toLowerCase()));

        if (!shouldPreserve) {
          localStorage.removeItem(key);
          console.log('Cleared localStorage key:', key);
        }
      });
    }

    // Clear all sessionStorage
    if ('sessionStorage' in window) {
      sessionStorage.clear();
      console.log('Cleared all sessionStorage');
    }
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

/**
 * Forces service worker to update and take control
 */
export const forceServiceWorkerUpdate = async (): Promise<boolean> => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        // Force update
        await registration.update();

        // If there's a waiting service worker, activate it
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        return true;
      }
    }
    return false;
  } catch (error) {
    console.error('Error updating service worker:', error);
    return false;
  }
};

/**
 * Complete PWA update process
 * Clears all caches, storage, and forces reload
 */
export const performCompleteUpdate = async (): Promise<void> => {
  console.log('Starting complete PWA update...');

  try {
    // Step 1: Clear all caches
    await clearAllCaches();

    // Step 2: Clear storage selectively
    clearStorageSelectively();

    // Step 3: Force service worker update
    await forceServiceWorkerUpdate();

    // Step 4: Wait for operations to complete
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Step 5: Force hard reload
    forceHardReload();
  } catch (error) {
    console.error('Error during complete update:', error);
    // Fallback to hard reload
    forceHardReload();
  }
};

/**
 * Check if app version has changed
 */
export const checkVersionChange = async (): Promise<{ hasChanged: boolean; newVersion?: string }> => {
  try {
    const currentVersion = localStorage.getItem('app_version');

    // Get version from service worker
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration?.active) {
        return new Promise((resolve) => {
          const messageChannel = new MessageChannel();

          messageChannel.port1.onmessage = (event) => {
            if (event.data && event.data.type === 'VERSION_INFO') {
              const newVersion = event.data.version;
              const hasChanged = currentVersion !== newVersion;

              if (hasChanged) {
                localStorage.setItem('app_version', newVersion);
              }

              resolve({ hasChanged, newVersion });
            }
          };

          registration.active?.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
        });
      }
    }

    return { hasChanged: false };
  } catch (error) {
    console.error('Error checking version:', error);
    return { hasChanged: false };
  }
};
